import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const LivePlayer = lazy(() => import('./LivePlayer'));

/**
 * Live TV: channels rather than fixtures. Three kinds, one list: the
 * hand-picked ones in config/live-tv.json, the always-on sports networks the
 * stream aggregator lists, and the iptv-org catalogue - every free channel
 * on earth that the server has found playing from the house (iptv.js). Each
 * opens the same player the Sports tab uses.
 *
 * That is thousands of channels, so the page is by country: the household's
 * own countries first, a dozen tiles each, and "See all" to open one. A
 * search or a chosen country lists everything that matches, a page at a time.
 */

// Category tabs, in this order; the rest sit in the "More" menu
const MAIN_GROUPS = ['Sport', 'News', 'Entertainment', 'Movies', 'Series', 'Kids', 'Music', 'Documentary', 'General'];
// Countries shown first, before the rest by size
const HOME_REGIONS = ['Sports networks', 'Canada', 'United States', 'United Kingdom', 'International', 'Ireland', 'Australia', 'France'];
const PER_COUNTRY = 12;
const COUNTRIES_PAGE = 10;
const LIST_PAGE = 120;
const PREF_GROUP = 'tv.group';
const PREF_REGION = 'tv.region';

const remembered = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (_) {
    return fallback;
  }
};

const remember = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    // Private mode; nothing lost
  }
};

const initials = (name) =>
  name
    .replace(/[^A-Za-z0-9 ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('') || '?';

// A stable hue per channel name, so a tile without a logo still has a colour
// of its own
const hueOf = (name) => {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
};

const orderOf = (list, value) => {
  const at = list.indexOf(value);
  return at === -1 ? list.length : at;
};

// The aggregator's channels carry their sport as a region; they read better
// together under one heading of their own
const regionOf = (channel) => (channel.id.startsWith('agg:') ? 'Sports networks' : channel.region || 'International');

// Hand-picked channels before the catalogue's, then by name
const isPicked = (channel) => !channel.id.startsWith('channel:iptv-');
const byPickThenName = (a, b) => Number(isPicked(b)) - Number(isPicked(a)) || a.name.localeCompare(b.name);

const asItem = (channel) => ({
  id: channel.id,
  kind: 'channel',
  name: channel.name,
  shortName: channel.name,
  state: 'in',
  status: { state: 'in', short: 'Live', detail: null },
  league: { name: [channel.group, regionOf(channel)].filter(Boolean).join(' · ') },
  home: null,
  away: null,
  streams: channel.streams
});

/**
 * Is this logo mostly white? Broadcasters draw their marks for their own
 * dark players, so half the logos are white on transparent and vanish on a
 * white tile. The logo is served from this origin, so a canvas can look at
 * its pixels: bright ones go on a dark tile, the rest on white.
 */
const isLight = (image) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 24;
    canvas.height = 24;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, 24, 24);
    const { data } = context.getImageData(0, 0, 24, 24);
    let sum = 0;
    let count = 0;
    for (let at = 0; at < data.length; at += 4) {
      if (data[at + 3] < 40) continue;
      sum += (data[at] + data[at + 1] + data[at + 2]) / 3;
      count += 1;
    }
    return count > 0 && sum / count > 170;
  } catch (_) {
    return false;
  }
};

const Tile = ({ channel, meta, onWatch, starred, onStar }) => {
  const [broken, setBroken] = useState(false);
  const [light, setLight] = useState(false);
  const art = channel.logo || channel.poster;
  return (
    <div className="tv-tile-wrap">
    <button type="button" className="tv-tile" onClick={() => onWatch(channel)} style={{ '--hue': hueOf(channel.name) }}>
      <span className={`tv-tile-art${art && !broken ? ' has-logo' : ''}${light ? ' is-light' : ''}`}>
        {art && !broken ? (
          <img src={art} alt="" loading="lazy" onError={() => setBroken(true)} onLoad={(event) => setLight(isLight(event.target))} />
        ) : (
          <span className="tv-tile-initials">{initials(channel.name)}</span>
        )}
      </span>
      <span className="tv-tile-body">
        <span className="tv-tile-name">{channel.name}</span>
        <span className="tv-tile-meta">{channel.direct ? meta : `Mirrors · ${channel.streams.length}`}</span>
      </span>
    </button>
    {/* A sibling, not inside the tile: a button can't hold a button */}
    <button
      type="button"
      className={`tv-star${starred ? ' is-on' : ''}`}
      onClick={() => onStar(channel, !starred)}
      aria-label={starred ? `Unstar ${channel.name}` : `Star ${channel.name}`}
      aria-pressed={starred}
      title={starred ? 'Starred' : 'Star this channel'}
    >
      {starred ? '★' : '☆'}
    </button>
    </div>
  );
};

const LiveTv = () => {
  const [channels, setChannels] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [error, setError] = useState('');
  const [watching, setWatching] = useState(null);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(() => remembered(PREF_GROUP, 'All'));
  const [region, setRegion] = useState(() => remembered(PREF_REGION, 'All'));
  const [listLimit, setListLimit] = useState(LIST_PAGE);
  const [countryLimit, setCountryLimit] = useState(COUNTRIES_PAGE);
  // Starred channel ids, per profile and shared with the TV apps
  // (/api/library/starred-channels); they lead the page as their own section
  const [starred, setStarred] = useState(() => new Set());

  useEffect(() => remember(PREF_GROUP, group), [group]);
  useEffect(() => remember(PREF_REGION, region), [region]);
  // A new filter starts at the top of its list
  useEffect(() => {
    setListLimit(LIST_PAGE);
    setCountryLimit(COUNTRIES_PAGE);
  }, [group, region, query]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/live/tv')
      .then(({ data }) => {
        if (cancelled) return;
        setChannels(data.data.channels || []);
        setCatalogue(data.data.catalogue || null);
      })
      .catch((err) => !cancelled && setError(err?.response?.data?.error || err.message || 'Could not load channels'));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/library/starred-channels')
      .then(({ data }) => !cancelled && setStarred(new Set(data.data || [])))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const star = (channel, on) => {
    // Shown at once, put right if the server disagrees
    setStarred((current) => {
      const next = new Set(current);
      if (on) next.add(channel.id);
      else next.delete(channel.id);
      return next;
    });
    const call = on ? axios.put : axios.delete;
    call(`/api/library/starred-channels/${encodeURIComponent(channel.id)}`)
      .then(({ data }) => setStarred(new Set(data.data || [])))
      .catch(() => {});
  };

  const groupCounts = useMemo(() => {
    const counts = new Map();
    for (const channel of channels || []) counts.set(channel.group, (counts.get(channel.group) || 0) + 1);
    return counts;
  }, [channels]);

  const tabs = MAIN_GROUPS.filter((name) => groupCounts.has(name));
  const more = [...groupCounts.keys()].filter((name) => !MAIN_GROUPS.includes(name)).sort((a, b) => a.localeCompare(b));

  // Channels in the chosen category, matched against the search
  const inGroup = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (channels || []).filter(
      (channel) =>
        (group === 'All' || channel.group === group) &&
        (!needle || channel.name.toLowerCase().includes(needle) || regionOf(channel).toLowerCase().includes(needle) || (channel.group || '').toLowerCase().includes(needle))
    );
  }, [channels, group, query]);

  // Countries for the menu and the page, home ones first, then the biggest
  const regions = useMemo(() => {
    const counts = new Map();
    for (const channel of inGroup) counts.set(regionOf(channel), (counts.get(regionOf(channel)) || 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => orderOf(HOME_REGIONS, a[0]) - orderOf(HOME_REGIONS, b[0]) || b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [inGroup]);

  const flat = Boolean(query.trim()) || region !== 'All';
  const listed = useMemo(
    () => (flat ? inGroup.filter((channel) => region === 'All' || regionOf(channel) === region).sort(byPickThenName) : []),
    [flat, inGroup, region]
  );
  const sections = useMemo(
    () =>
      flat
        ? []
        : regions.slice(0, countryLimit).map(({ name, count }) => ({
            name,
            count,
            channels: inGroup.filter((channel) => regionOf(channel) === name).sort(byPickThenName).slice(0, PER_COUNTRY)
          })),
    [flat, regions, countryLimit, inGroup]
  );

  const starredHere = useMemo(
    () => inGroup.filter((channel) => starred.has(channel.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [inGroup, starred]
  );

  const total = channels?.length || 0;
  const checking = catalogue?.checking;
  const watch = (chosen) => setWatching(asItem(chosen));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Live TV</h1>
          <p className="page-sub">
            {total ? `${total.toLocaleString()} channels from ${regions.length || '…'} places, every one checked from home` : 'Channels on all day'}
            {checking ? ` · checking more now (${Math.round((checking.done / checking.total) * 100)}%)` : ''}
          </p>
        </div>
      </div>

      <div className="tv-bar">
        <div className="tabs tv-tabs">
          <button type="button" className={`tab${group === 'All' ? ' is-active' : ''}`} onClick={() => setGroup('All')}>
            All
          </button>
          {tabs.map((name) => (
            <button key={name} type="button" className={`tab${group === name ? ' is-active' : ''}`} onClick={() => setGroup(name)}>
              {name}
              <span className="tab-count">{groupCounts.get(name)}</span>
            </button>
          ))}
          {more.length ? (
            <select
              className={`tv-select tv-select--tab${more.includes(group) ? ' is-active' : ''}`}
              value={more.includes(group) ? group : ''}
              onChange={(event) => setGroup(event.target.value || 'All')}
              aria-label="More categories"
            >
              <option value="">More…</option>
              {more.map((name) => (
                <option key={name} value={name}>
                  {name} ({groupCounts.get(name)})
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <div className="tv-filters">
          <select className="tv-select" value={region} onChange={(event) => setRegion(event.target.value)} aria-label="Country">
            <option value="All">Every country</option>
            {regions.map(({ name, count }) => (
              <option key={name} value={name}>
                {name} ({count})
              </option>
            ))}
            {region !== 'All' && !regions.some((entry) => entry.name === region) ? <option value={region}>{region} (0)</option> : null}
          </select>
          <input
            type="search"
            className="tv-search"
            placeholder="Find a channel"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Find a channel"
          />
        </div>
      </div>

      {error && <div className="alert">{error}</div>}

      {channels === null && !error ? (
        <div className="loading">LOADING</div>
      ) : flat ? (
        listed.length ? (
          <section className="live-section tv-section">
            <div className="live-section-head">
              <h2 className="live-section-title">
                {query.trim() ? `"${query.trim()}"` : region}
                {query.trim() && region !== 'All' ? ` in ${region}` : ''}
                <span className="live-section-count">{listed.length}</span>
              </h2>
              {region !== 'All' ? (
                <button type="button" className="live-section-toggle" onClick={() => setRegion('All')}>
                  Every country
                </button>
              ) : null}
            </div>
            <div className="tv-grid">
              {listed.slice(0, listLimit).map((channel) => (
                <Tile key={channel.id} channel={channel} meta={region === 'All' ? regionOf(channel) : channel.group} onWatch={watch} starred={starred.has(channel.id)} onStar={star} />
              ))}
            </div>
            {listed.length > listLimit ? (
              <button type="button" className="btn btn--soft btn--sm tv-more" onClick={() => setListLimit((limit) => limit + LIST_PAGE)}>
                Show {Math.min(LIST_PAGE, listed.length - listLimit)} more of {listed.length - listLimit}
              </button>
            ) : null}
          </section>
        ) : (
          <div className="empty">{query ? `Nothing called "${query}"${region !== 'All' ? ` in ${region}` : ''}.` : `No ${group === 'All' ? '' : `${group} `}channels in ${region}.`}</div>
        )
      ) : sections.length ? (
        <>
          {starredHere.length ? (
            <section className="live-section tv-section">
              <div className="live-section-head">
                <h2 className="live-section-title">
                  Starred
                  <span className="live-section-count">{starredHere.length}</span>
                </h2>
              </div>
              <div className="tv-grid">
                {starredHere.map((channel) => (
                  <Tile key={channel.id} channel={channel} meta={regionOf(channel)} onWatch={watch} starred onStar={star} />
                ))}
              </div>
            </section>
          ) : null}
          {sections.map((section) => (
            <section key={section.name} className="live-section tv-section">
              <div className="live-section-head">
                <h2 className="live-section-title">
                  {section.name}
                  <span className="live-section-count">{section.count}</span>
                </h2>
                {section.count > section.channels.length ? (
                  <button type="button" className="live-section-toggle" onClick={() => setRegion(section.name)}>
                    See all {section.count}
                  </button>
                ) : null}
              </div>
              <div className="tv-grid">
                {section.channels.map((channel) => (
                  <Tile key={channel.id} channel={channel} meta={channel.group} onWatch={watch} starred={starred.has(channel.id)} onStar={star} />
                ))}
              </div>
            </section>
          ))}
          {regions.length > countryLimit ? (
            <button type="button" className="btn btn--soft btn--sm tv-more" onClick={() => setCountryLimit((limit) => limit + COUNTRIES_PAGE)}>
              Show more countries ({regions.length - countryLimit} to go)
            </button>
          ) : null}
        </>
      ) : (
        <div className="empty">No channels here yet. The server checks the catalogue in the background; try again in a few minutes.</div>
      )}

      {watching ? (
        <Suspense fallback={null}>
          <LivePlayer item={watching} onClose={() => setWatching(null)} />
        </Suspense>
      ) : null}
    </div>
  );
};

export default LiveTv;
